/**
 * Patient Before/After Consent Page — TREATX-L1E COMPLIANCE CLOSURE
 * =================================================================
 * Token-only, exact-media, evidence-binding.
 *
 * The patient MUST reach this page via a signed deep-link token issued
 * by the clinic (`/before-after/consent-request/{token}`). The former
 * free-form `?clinic_id=…` legacy path has been removed — legacy data
 * was test data and no compatibility work is required.
 *
 * Backend SSOT:
 *   GET  /api/before-after/consent-requests/{token}/context
 *        → server-authoritative: clinic brand, treatment, exact before/after
 *          media URLs + SHA-256 evidence, regulatory dates, provider attestations.
 *   POST /api/before-after/consent-requests/{token}/respond
 *        → binds the exact media pair + hashes into `user_consents.metadata`.
 *   GET  /api/before-after/consent/mine                      (list)
 *   POST /api/before-after/consent/{id}/withdraw             (audit event)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft, ShieldCheck, ShieldOff, Loader2, CheckCircle2,
  AlertTriangle, FileText, Clock,
} from 'lucide-react';
import api from '../../utils/api';
import AuthMediaThumb from '../../components/AuthMediaThumb';

const BeforeAfterConsentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const params = useParams();

  const [view, setView] = useState('form'); // 'form' | 'receipt' | 'list'
  const [submitting, setSubmitting] = useState(false);

  const [typedSignature, setTypedSignature] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [issuedConsent, setIssuedConsent] = useState(null);

  const [consents, setConsents] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [withdrawingId, setWithdrawingId] = useState(null);

  // TREATX-L1E: token is REQUIRED. Accepted transports (in priority order):
  //   1. Path param   /before-after/consent-request/:token       (canonical, matches email deep-link)
  //   2. `?req=…`     (legacy)
  //   3. `?token=…`   (legacy)
  const reqToken = params.token || search.get('req') || search.get('token') || '';
  const [reqCtx, setReqCtx] = useState(null);
  const [reqError, setReqError] = useState('');

  const fetchContext = useCallback(() => {
    if (!reqToken) return;
    setReqError('');
    setReqCtx(null);
    api
      .get(`/before-after/consent-requests/${reqToken}/context`)
      .then((r) => setReqCtx(r.data || null))
      .catch((e) => setReqError(e?.response?.data?.error || e?.response?.data?.code || 'REQUEST_NOT_FOUND'));
  }, [reqToken]);

  useEffect(() => {
    if (!reqToken) return;
    setView('form');
    setIssuedConsent(null);
    fetchContext();
  }, [reqToken, fetchContext]);

  const respondToRequest = async (accept) => {
    if (!reqToken) return;
    if (accept && !typedSignature.trim()) {
      toast.error(t('patient.beforeAfterConsent.errors.signatureRequired'));
      return;
    }
    if (accept && !consentChecked) {
      toast.error(t('patient.beforeAfterConsent.errors.consentBoxRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/before-after/consent-requests/${reqToken}/respond`, {
        accept,
        typed_signature: typedSignature,
      });
      if (res.data?.success) {
        setIssuedConsent({
          consent_id: res.data?.consent_id || null,
          accepted: !!res.data?.accepted,
          clinic_name: reqCtx?.clinic_display_name || '',
          treatment_slug: reqCtx?.treatment_slug || null,
          consented_at: new Date().toISOString(),
        });
        setView('receipt');
      }
    } catch (e) {
      setReqError(e?.response?.data?.error || e?.response?.data?.code || 'RESPONSE_FAILED');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchMyConsents = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await api.get('/before-after/consent/mine');
      setConsents(r.data?.consents || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error(t('patient.beforeAfterConsent.errors.loginRequired'));
      } else {
        toast.error(err?.response?.data?.message || t('patient.beforeAfterConsent.errors.loadListFailed'));
      }
    } finally {
      setListLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (view === 'list') fetchMyConsents();
  }, [view, fetchMyConsents]);

  const withdraw = async (cid, activeCount) => {
    const msg = activeCount > 0
      ? t('patient.beforeAfterConsent.confirms.withdrawWithCases', { count: activeCount })
      : t('patient.beforeAfterConsent.confirms.withdrawEmpty');
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setWithdrawingId(cid);
    try {
      const r = await api.post(`/before-after/consent/${cid}/withdraw`);
      if (!r.data?.success) throw new Error(r.data?.message || 'Withdrawal failed');
      toast.success(t('patient.beforeAfterConsent.toasts.withdrawn'));
      fetchMyConsents();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t('patient.beforeAfterConsent.errors.withdrawFailed'));
    } finally {
      setWithdrawingId(null);
    }
  };

  const attestations = reqCtx?.provider_attestations || {};
  const attestationLines = [
    { key: 'same_environment_confirmed', label: t('patient.beforeAfterConsent.attestations.sameEnvironment') },
    { key: 'no_retouch_confirmed', label: t('patient.beforeAfterConsent.attestations.noRetouch') },
    { key: 'no_privacy_violation_confirmed', label: t('patient.beforeAfterConsent.attestations.noPrivacyViolation') },
    { key: 'no_incentive_offered_confirmed', label: t('patient.beforeAfterConsent.attestations.noIncentive') },
  ];

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return formatDateLocale(iso); } catch { return String(iso).slice(0, 10); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/patient/dashboard')}
          className="mb-2 -ml-3"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('patient.beforeAfterConsent.back')}
        </Button>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-emerald-600" />
              {t('patient.beforeAfterConsent.title')}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {t('patient.beforeAfterConsent.subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={view === 'form' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('form')}
              data-testid="tab-form"
            >
              {t('patient.beforeAfterConsent.tabs.request')}
            </Button>
            <Button
              variant={view === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('list')}
              data-testid="tab-list"
            >
              {t('patient.beforeAfterConsent.tabs.mine')}
            </Button>
          </div>
        </div>

        {/* ==================== TOKEN-ONLY FORM ==================== */}
        {view === 'form' && (
          <Card>
            <CardContent className="p-6 space-y-5" data-testid="consent-form">
              {!reqToken ? (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
                  data-testid="ba-consent-no-token"
                >
                  <strong>{t('patient.beforeAfterConsent.noToken.title')}</strong>
                  <p className="mt-1">{t('patient.beforeAfterConsent.noToken.body')}</p>
                </div>
              ) : reqError ? (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
                  data-testid="ba-request-error"
                >
                  {t('patient.beforeAfterConsent.errors.loadRequest', { code: reqError })}
                </div>
              ) : !reqCtx ? (
                <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="ba-request-loading">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('patient.beforeAfterConsent.loading')}
                </div>
              ) : (
                <>
                  <div
                    className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900"
                    data-testid="ba-request-banner"
                  >
                    {t('patient.beforeAfterConsent.banner', { clinic: reqCtx.clinic_display_name })}
                  </div>

                  {/* EXACT media pair */}
                  <section className="space-y-2" data-testid="ba-request-media-pair">
                    <h2 className="text-base font-semibold text-slate-900">
                      {t('patient.beforeAfterConsent.mediaHeading')}
                    </h2>
                    <p className="text-xs text-slate-600">
                      {t('patient.beforeAfterConsent.mediaSubheading')}
                    </p>
                    {reqCtx.before_media_id && reqCtx.after_media_id ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border border-slate-200 overflow-hidden">
                          <div className="aspect-square bg-slate-100 flex items-center justify-center text-slate-400">
                            <AuthMediaThumb
                              mediaId={reqCtx.before_media_id}
                              url={reqCtx.before_media_url}
                              variant="preview"
                              alt={t('patient.beforeAfterConsent.mediaBeforeAlt')}
                              testIdPrefix="ba-request-before"
                            />
                          </div>
                          <div className="p-2 text-center text-xs font-medium text-slate-700">
                            {t('patient.beforeAfterConsent.mediaBeforeLabel')}
                          </div>
                        </div>
                        <div className="rounded-md border border-slate-200 overflow-hidden">
                          <div className="aspect-square bg-slate-100 flex items-center justify-center text-slate-400">
                            <AuthMediaThumb
                              mediaId={reqCtx.after_media_id}
                              url={reqCtx.after_media_url}
                              variant="preview"
                              alt={t('patient.beforeAfterConsent.mediaAfterAlt')}
                              testIdPrefix="ba-request-after"
                            />
                          </div>
                          <div className="p-2 text-center text-xs font-medium text-slate-700">
                            {t('patient.beforeAfterConsent.mediaAfterLabel')}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                        data-testid="ba-request-media-missing"
                      >
                        {t('patient.beforeAfterConsent.mediaMissing')}
                      </div>
                    )}
                  </section>

                  {/* Context: clinic + treatment + dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ba-request-context-cards">
                    <div className="bg-white border rounded-lg p-3">
                      <div className="text-[10px] uppercase text-slate-500">
                        {t('patient.beforeAfterConsent.contextLabels.clinic')}
                      </div>
                      <div className="text-sm font-semibold text-slate-900" data-testid="ba-request-clinic">
                        {reqCtx.clinic_display_name}
                      </div>
                    </div>
                    <div className="bg-white border rounded-lg p-3">
                      <div className="text-[10px] uppercase text-slate-500">
                        {t('patient.beforeAfterConsent.contextLabels.treatment')}
                      </div>
                      <div className="text-sm font-semibold text-slate-900" data-testid="ba-request-treatment">
                        {reqCtx.treatment_slug}
                      </div>
                    </div>
                    <div className="bg-white border rounded-lg p-3">
                      <div className="text-[10px] uppercase text-slate-500">
                        {t('patient.beforeAfterConsent.contextLabels.procedureDate')}
                      </div>
                      <div className="text-sm font-semibold text-slate-900" data-testid="ba-request-procedure-date">
                        {fmtDate(reqCtx.procedure_date)}
                      </div>
                    </div>
                  </div>

                  {/* Provider attestations (read-only) */}
                  <section className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="ba-request-attestations">
                    <h2 className="text-sm font-semibold text-slate-900">
                      {t('patient.beforeAfterConsent.attestationsHeading')}
                    </h2>
                    <ul className="text-sm text-slate-700 space-y-1.5">
                      {attestationLines.map((row) => (
                        <li key={row.key} className="flex items-start gap-2" data-testid={`ba-attestation-${row.key}`}>
                          {attestations[row.key] ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          )}
                          <span>{row.label}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Canonical legal text + disclaimer */}
                  <section className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="ba-request-legal">
                    <p className="font-semibold mb-1">{t('patient.beforeAfterConsent.legalHeading')}</p>
                    <p>{t('patient.beforeAfterConsent.legalBody')}</p>
                    <p className="mt-2 text-xs text-slate-600">{t('patient.beforeAfterConsent.disclaimer')}</p>
                  </section>

                  {reqCtx.state !== 'pending' && (
                    <p className="text-xs text-slate-500" data-testid="ba-request-already-state">
                      {t('patient.beforeAfterConsent.alreadyResponded', { state: reqCtx.state })}
                    </p>
                  )}

                  {reqCtx.state === 'pending' && (
                    <>
                      <div>
                        <Label htmlFor="typed-signature">{t('patient.beforeAfterConsent.signatureLabel')}</Label>
                        <Input
                          id="typed-signature"
                          value={typedSignature}
                          onChange={(e) => setTypedSignature(e.target.value)}
                          placeholder={t('patient.beforeAfterConsent.signaturePlaceholder')}
                          maxLength={120}
                          data-testid="ba-request-signature"
                        />
                      </div>
                      <label
                        className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer"
                        data-testid="consent-checkbox-label"
                      >
                        <input
                          type="checkbox"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          className="mt-1"
                          data-testid="consent-checkbox"
                        />
                        <span className="text-sm text-slate-800">
                          {t('patient.beforeAfterConsent.consentCheckbox')}
                        </span>
                      </label>
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={() => respondToRequest(true)}
                          disabled={submitting || !typedSignature.trim() || !consentChecked || !reqCtx.before_media_id || !reqCtx.after_media_id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="ba-request-accept-btn"
                        >
                          {submitting
                            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('patient.beforeAfterConsent.submitting')}</>
                            : t('patient.beforeAfterConsent.approveBtn')}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => respondToRequest(false)}
                          disabled={submitting}
                          data-testid="ba-request-decline-btn"
                        >
                          {t('patient.beforeAfterConsent.declineBtn')}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ==================== RECEIPT ==================== */}
        {view === 'receipt' && issuedConsent && (
          <Card className={issuedConsent.accepted === false ? 'border-rose-200' : 'border-emerald-200'}>
            <CardContent className="p-6 space-y-4" data-testid="consent-receipt">
              <div className="flex items-center gap-3">
                {issuedConsent.accepted === false ? (
                  <ShieldOff className="h-8 w-8 text-rose-600" />
                ) : (
                  <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                )}
                <div>
                  <h2 className="text-xl font-semibold text-slate-900" data-testid="receipt-headline">
                    {issuedConsent.accepted === false
                      ? t('patient.beforeAfterConsent.receipt.declinedTitle')
                      : t('patient.beforeAfterConsent.receipt.recordedTitle')}
                  </h2>
                  <p className="text-sm text-slate-600">
                    {issuedConsent.accepted === false
                      ? t('patient.beforeAfterConsent.receipt.declinedBody')
                      : t('patient.beforeAfterConsent.receipt.recordedBody')}
                  </p>
                </div>
              </div>
              <div className="rounded-md border border-slate-200 p-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">{t('patient.beforeAfterConsent.contextLabels.clinic')}</span>
                  <span className="font-medium text-slate-900" data-testid="receipt-clinic">
                    {issuedConsent.clinic_name || '—'}
                  </span>
                </div>
                {issuedConsent.treatment_slug && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">{t('patient.beforeAfterConsent.contextLabels.treatment')}</span>
                    <span className="font-medium text-slate-900" data-testid="receipt-treatment">
                      {issuedConsent.treatment_slug}
                    </span>
                  </div>
                )}
                {issuedConsent.consent_id && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">{t('patient.beforeAfterConsent.receipt.consentId')}</span>
                    <span className="font-mono text-xs break-all" data-testid="receipt-consent-id">
                      {issuedConsent.consent_id}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">{t('patient.beforeAfterConsent.receipt.recordedAt')}</span>
                  <span className="text-slate-700" data-testid="receipt-timestamp">
                    {issuedConsent.consented_at ? formatDateTimeLocale(issuedConsent.consented_at) : '—'}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setView('list')} data-testid="see-all-btn">
                  {t('patient.beforeAfterConsent.receipt.seeAll')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ==================== LIST ==================== */}
        {view === 'list' && (
          <Card>
            <CardContent className="p-6 space-y-4" data-testid="consent-list">
              <div
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2"
                data-testid="withdraw-warning"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t('patient.beforeAfterConsent.withdrawWarning')}</span>
              </div>

              {listLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t('patient.beforeAfterConsent.loading')}
                </div>
              ) : consents.length === 0 ? (
                <div className="py-12 text-center text-slate-500" data-testid="consent-list-empty">
                  <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p>{t('patient.beforeAfterConsent.emptyList')}</p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="consent-list-rows">
                  {consents.map((c) => (
                    <li
                      key={c.consent_id}
                      className="py-3 flex items-start justify-between gap-4"
                      data-testid={`consent-row-${c.consent_id}`}
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{c.clinic_name}</span>
                          {c.withdrawn ? (
                            <Badge className="bg-rose-100 text-rose-700 border-0">
                              {t('patient.beforeAfterConsent.statusWithdrawn')}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">
                              {t('patient.beforeAfterConsent.statusActive')}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {t('patient.beforeAfterConsent.linkedCases', {
                              active: c.linked_cases_active,
                              total: c.linked_cases_total,
                            })}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                          <Clock className="h-3 w-3" />
                          <span>
                            {t('patient.beforeAfterConsent.recordedAt', {
                              date: c.consented_at?.slice(0, 19)?.replace('T', ' '),
                            })}
                          </span>
                          {c.withdrawn && c.withdrawn_at && (
                            <span className="text-rose-600">
                              · {t('patient.beforeAfterConsent.withdrawnAt', {
                                date: c.withdrawn_at.slice(0, 19).replace('T', ' '),
                              })}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono break-all">{c.consent_id}</div>
                      </div>
                      {!c.withdrawn && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => withdraw(c.consent_id, c.linked_cases_active || 0)}
                          disabled={withdrawingId === c.consent_id}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0"
                          data-testid={`withdraw-${c.consent_id}`}
                        >
                          {withdrawingId === c.consent_id
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> {t('patient.beforeAfterConsent.withdrawing')}</>
                            : <><ShieldOff className="h-3.5 w-3.5 mr-1" /> {t('patient.beforeAfterConsent.withdrawBtn')}</>}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BeforeAfterConsentPage;
